import React, { useEffect, useRef, useState } from "react";
import { Navigation, MapPin, Clock, User, Ruler, X, Check, ChevronRight } from "lucide-react";

export default function BookingAlertOverlay({ booking, isVisible, onAccept, onCancel, isProcessing }) {
  const [sliderProgress, setSliderProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [sliderWidth, setSliderWidth] = useState(0);
  const sliderRef = useRef(null);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);
  
  const fromValue = booking?.From || booking?.fromLocation || booking?.WorkArea || "";
  const toValue = booking?.TO || booking?.toLocation || "";
  const workScopeValue = booking?.WorkScope || booking?.workScope || "";
  const scheduledTimeValue = booking?.bsTime || booking?.bookedDate || "";

  // Play sound when overlay becomes visible - USING OLD SOUND PATTERN
  useEffect(() => {
    if (isVisible && booking) {
      console.log("BookingAlertOverlay: Playing sound immediately");
      
      // Stop any existing audio first
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      // Use Web Audio API for immediate, reliable sound
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        
        // Resume audio context if suspended (browser autoplay policy)
        if (audioContext.state === 'suspended') {
          audioContext.resume();
        }
        
        const playRingTone = () => {
          const now = audioContext.currentTime;
          
          // Uber-style attention chime - pleasant but urgent
          // First chime (ding)
          const osc1 = audioContext.createOscillator();
          const gain1 = audioContext.createGain();
          osc1.connect(gain1);
          gain1.connect(audioContext.destination);
          osc1.frequency.value = 880; // A5
          osc1.type = "sine";
          gain1.gain.setValueAtTime(0.7, now);
          gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
          osc1.start(now);
          osc1.stop(now + 0.3);

          // Second chime (higher ding)
          const osc2 = audioContext.createOscillator();
          const gain2 = audioContext.createGain();
          osc2.connect(gain2);
          gain2.connect(audioContext.destination);
          osc2.frequency.value = 1318.51; // E6
          osc2.type = "sine";
          gain2.gain.setValueAtTime(0.7, now + 0.15);
          gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
          osc2.start(now + 0.15);
          osc2.stop(now + 0.5);

          // Third sustained tone (attention grabber)
          const osc3 = audioContext.createOscillator();
          const gain3 = audioContext.createGain();
          osc3.connect(gain3);
          gain3.connect(audioContext.destination);
          osc3.frequency.value = 1760; // A6
          osc3.type = "sine";
          gain3.gain.setValueAtTime(0.6, now + 0.35);
          gain3.gain.exponentialRampToValueAtTime(0.01, now + 0.8);
          osc3.start(now + 0.35);
          osc3.stop(now + 0.8);

          // Soft low undertone for richness
          const osc4 = audioContext.createOscillator();
          const gain4 = audioContext.createGain();
          osc4.connect(gain4);
          gain4.connect(audioContext.destination);
          osc4.frequency.value = 440; // A4
          osc4.type = "triangle";
          gain4.gain.setValueAtTime(0.3, now);
          gain4.gain.exponentialRampToValueAtTime(0.01, now + 0.6);
          osc4.start(now);
          osc4.stop(now + 0.6);
        };

        // Play immediately
        playRingTone();
        
        // Repeat every 2 seconds
        intervalRef.current = setInterval(playRingTone, 2000);
        
        // Store reference for cleanup
        audioRef.current = { 
          context: audioContext,
          pause: () => {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          }
        };

        console.log("BookingAlertOverlay: Ring tone started");

      } catch (e) {
        console.error("Web Audio API failed:", e);
      }

      // Vibrate on mobile
      if (navigator.vibrate) {
        navigator.vibrate([400, 200, 400, 200, 400]);
      }
    }

    // Cleanup when overlay closes or component unmounts
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [isVisible, booking]);

  // Handle slider drag
  const handleDragStart = (e) => {
    if (isProcessing) return;
    e.preventDefault();
    setIsDragging(true);
    const clientX = e.type === "touchstart" ? e.touches[0].clientX : e.clientX;
    setDragStartX(clientX);
  };

  const handleDragMove = (e) => {
    if (!isDragging || isProcessing) return;
    e.preventDefault();
    
    const clientX = e.type === "touchmove" ? e.touches[0].clientX : e.clientX;
    const deltaX = clientX - dragStartX;
    const maxWidth = sliderWidth - 56; // Subtract thumb width
    let progress = Math.min(Math.max(0, (deltaX / maxWidth) * 100), 100);
    
    setSliderProgress(progress);
    
    // Auto-accept when slider reaches 100%
    if (progress >= 95 && !isProcessing) {
      handleAccept();
    }
  };

  const handleDragEnd = () => {
    if (!isDragging || isProcessing) return;
    setIsDragging(false);
    // Reset slider if not completed
    if (sliderProgress < 95) {
      setSliderProgress(0);
    }
  };

  // Measure slider width
  useEffect(() => {
    if (sliderRef.current) {
      setSliderWidth(sliderRef.current.offsetWidth);
    }
  }, [isVisible]);

  // Add global event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleDragMove);
      window.addEventListener("mouseup", handleDragEnd);
      window.addEventListener("touchmove", handleDragMove, { passive: false });
      window.addEventListener("touchend", handleDragEnd);
    }

    return () => {
      window.removeEventListener("mousemove", handleDragMove);
      window.removeEventListener("mouseup", handleDragEnd);
      window.removeEventListener("touchmove", handleDragMove);
      window.removeEventListener("touchend", handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Stop sound and accept
  const handleAccept = () => {
    console.log("BookingAlertOverlay: Accept clicked - stopping sound");
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    onAccept();
  };

  // Stop sound and cancel
  const handleCancel = () => {
    console.log("BookingAlertOverlay: Cancel clicked - stopping sound");
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    onCancel();
  };

  if (!isVisible || !booking) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center px-3 sm:px-4 z-[9999]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleCancel}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-5 sm:px-6 py-4 sm:py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider">
                New Request
              </span>
            </div>
            <button
              onClick={handleCancel}
              disabled={isProcessing}
              className="p-1 hover:bg-white/10 rounded-lg transition"
            >
              <X size={16} className="text-gray-400" />
            </button>
          </div>
          <h2 className="text-white text-lg sm:text-xl font-semibold mt-2">
            New Trip Assignment
          </h2>
          <p className="text-gray-300 text-xs sm:text-sm mt-0.5">
            You have a new booking request
          </p>
        </div>

        {/* Content */}
        <div className="p-5 sm:p-6 space-y-4">
          {/* Booking ID Badge */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="bg-gray-100 rounded-lg px-2 py-1">
                <span className="text-xs font-mono font-medium text-gray-700">
                  #{booking.bId}
                </span>
              </div>
              <span className="text-xs text-gray-400">•</span>
              <span className="text-xs text-gray-500 capitalize">
                {booking.bookingType || "Real-time"}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Ruler size={12} className="text-gray-400" />
              <span className="text-xs text-gray-600">
                {booking.totalDestence ? `${booking.totalDestence} km` : "Calculating..."}
              </span>
            </div>
          </div>

          {/* Route Information */}
          <div className="space-y-3">
            {/* Pickup */}
            <div className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                  <MapPin size={14} className="text-emerald-600" />
                </div>
                <div className="w-0.5 h-8 bg-gray-200 mt-1" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 font-medium">PICKUP LOCATION</p>
                <p className="text-sm text-gray-900 mt-0.5">
                  {fromValue || "Location not specified"}
                </p>
              </div>
            </div>

            {/* Dropoff */}
            <div className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <Navigation size={14} className="text-blue-600" />
                </div>
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 font-medium">DROPOFF LOCATION</p>
                <p className="text-sm text-gray-900 mt-0.5">
                  {toValue || "Location not specified"}
                </p>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100" />

          {/* Additional Details */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-gray-50 rounded-lg">
                <User size={12} className="text-gray-500" />
              </div>
              <div>
                <p className="text-[10px] text-gray-400">EMPLOYEE</p>
                <p className="text-xs font-medium text-gray-700">
                  {booking.employeeName || "Employee"}
                </p>
              </div>
            </div>

            {scheduledTimeValue && (
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-gray-50 rounded-lg">
                  <Clock size={12} className="text-gray-500" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-400">SCHEDULED</p>
                  <p className="text-xs font-medium text-gray-700">
                    {scheduledTimeValue}
                  </p>
                </div>
              </div>
            )}
          </div>

          {workScopeValue && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">WORK SCOPE</p>
              <p className="text-sm text-gray-800">{workScopeValue}</p>
            </div>
          )}

          {/* Uber-style Slide to Accept */}
          <div className="mt-4">
            <div
              ref={sliderRef}
              className="relative bg-gray-100 rounded-xl h-14 overflow-hidden"
              onMouseDown={handleDragStart}
              onTouchStart={handleDragStart}
            >
              {/* Progress fill */}
              <div
                className="absolute left-0 top-0 h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all duration-100"
                style={{ width: `${sliderProgress}%` }}
              />
              
              {/* Slider content */}
              <div className="relative flex items-center justify-center h-full">
                {sliderProgress < 95 ? (
                  <>
                    <span className="text-sm font-medium text-gray-500">
                      Slide to accept trip
                    </span>
                    {/* Draggable thumb */}
                    <div
                      className={`
                        absolute left-0 top-1/2 -translate-y-1/2
                        w-12 h-12 bg-white rounded-xl shadow-md
                        flex items-center justify-center
                        transition-all duration-100
                        ${isDragging ? "scale-95" : "scale-100"}
                      `}
                      style={{ left: `calc(${sliderProgress}% - 6px)` }}
                    >
                      <ChevronRight size={20} className="text-gray-600" />
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span className="text-white font-medium text-sm">
                      Accepting...
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Decline option */}
            <button
              onClick={handleCancel}
              disabled={isProcessing}
              className="w-full mt-3 py-2.5 text-sm font-medium text-gray-500 
                       hover:text-red-600 hover:bg-red-50 rounded-lg
                       transition disabled:opacity-50"
            >
              Decline Trip
            </button>
          </div>
        </div>

        {/* Timer indicator */}
        <div className="bg-gray-50 px-5 sm:px-6 py-3 border-t border-gray-100">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Response needed</span>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              <span className="text-amber-600 font-medium">Waiting for response...</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}